import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, BarChart3, Globe, Loader2 } from 'lucide-react';

/**
 * Three chart cards shown on Admin Overview:
 * User Growth · Revenue Trend · Website Traffic.
 * Simple CSS bar charts (no chart library dependency).
 * Extracted from AdminOverviewTab.jsx (iter123 refactor).
 */
export const AdminOverviewCharts = ({ users, revenue, trends, loading }) => {
  return (
    <>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              User Growth
            </CardTitle>
            <CardDescription className="text-slate-600">
              New user signups over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-48 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-end gap-1 h-32">
                  {(users.new_users_trend || []).slice(-14).map((item, i) => {
                    const maxCount = Math.max(...(users.new_users_trend || []).map((t) => t.count), 1);
                    const height = (item.count / maxCount) * 100;
                    return (
                      <div
                        key={`user-bar-${item.date}-${i}`}
                        className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t hover:from-blue-600 hover:to-blue-500 transition-colors cursor-pointer group relative"
                        style={{ height: `${Math.max(height, 4)}%` }}
                        title={`${item.date}: ${item.count} users`}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                          {item.count}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{users.new_users_trend?.[0]?.date || 'Start'}</span>
                  <span>{users.new_users_trend?.[users.new_users_trend.length - 1]?.date || 'End'}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Chart */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Revenue Trend
            </CardTitle>
            <CardDescription className="text-slate-600">
              Daily revenue over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-48 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-end gap-1 h-32">
                  {(revenue.trend || []).slice(-14).map((item, i) => {
                    const maxAmount = Math.max(...(revenue.trend || []).map((t) => t.amount), 1);
                    const height = (item.amount / maxAmount) * 100;
                    return (
                      <div
                        key={`rev-bar-${item.date}-${i}`}
                        className="flex-1 bg-gradient-to-t from-green-500 to-emerald-400 rounded-t hover:from-green-600 hover:to-emerald-500 transition-colors cursor-pointer group relative"
                        style={{ height: `${Math.max(height, 4)}%` }}
                        title={`${item.date}: $${item.amount}`}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                          ${item.amount}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{revenue.trend?.[0]?.date || 'Start'}</span>
                  <span>{revenue.trend?.[revenue.trend.length - 1]?.date || 'End'}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Visitor Traffic Chart */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600" />
            Website Traffic
          </CardTitle>
          <CardDescription className="text-slate-600">
            Page views and unique visitors over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : trends.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-emerald-500" />
                  <span className="text-slate-600">Page Views</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-blue-500" />
                  <span className="text-slate-600">Unique Visitors</span>
                </div>
              </div>
              <div className="flex items-end gap-1 h-40">
                {trends.slice(-14).map((item, i) => {
                  const maxViews = Math.max(...trends.map((t) => t.page_views || 0), 1);
                  const pageViewsHeight = ((item.page_views || 0) / maxViews) * 100;
                  const visitorsHeight = ((item.unique_visitors || 0) / maxViews) * 100;
                  return (
                    <div key={`traffic-bar-${item.date}-${i}`} className="flex-1 flex items-end gap-0.5 group relative">
                      <div
                        className="flex-1 bg-emerald-500 rounded-t transition-colors hover:bg-emerald-600"
                        style={{ height: `${Math.max(pageViewsHeight, 4)}%` }}
                      />
                      <div
                        className="flex-1 bg-blue-500 rounded-t transition-colors hover:bg-blue-600"
                        style={{ height: `${Math.max(visitorsHeight, 4)}%` }}
                      />
                      <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                        <div>Views: {item.page_views || 0}</div>
                        <div>Visitors: {item.unique_visitors || 0}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-slate-500">
                <span>{trends[0]?.date || 'Start'}</span>
                <span>{trends[trends.length - 1]?.date || 'End'}</span>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-500">
              No visitor data available yet
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default AdminOverviewCharts;
